<template>
    <div class="bg-card rounded" v-if="weeks.current">
        <div class="card-header">
            <div class="row">
                <div class="col-4 text-start">
                    <button type="button" class="btn btn-sm btn-neutral" @click="changeWeek('previous')">
                        <i class="bi bi-arrow-left"></i>
                    </button>
                </div>
                <div class="col-4 text-center">
                    <h4>
                        {{ $trans.get('__JSON__.Current week: :value', { value: weeks.current.label }) }}
                        <span class="badge bg-primary" v-if="startOfWeek === weeks.now.start">Deze week</span>
                    </h4>
                </div>
                <div class="col-4 text-end">
                    <button type="button" class="btn btn-sm btn-neutral" @click="changeWeek('next')" :disabled="! weeks.next.label">
                        <i class="bi bi-arrow-right"></i>
                    </button>
                </div>
            </div>
        </div>
        <div class="p-5">
            <ul class="nav nav-tabs" id="myTab" role="tablist">
                <li class="nav-item" role="presentation" v-for="(name, day) in weekdays">
                    <a href="#" class="nav-link" :class="{ 'active': day === dayOfWeek }" @click="$event.preventDefault(); dayOfWeek = day">
                        {{ name }}
                    </a>
                </li>
            </ul>

            <div class="table-responsive">
                <table class="table table-hover table-nowrap">
                    <thead class="thead-light bg-gray-50">
                    <tr>
                        <th scope="col">
                            {{ $trans.get('__JSON__.Starts At') }}
                        </th>
                        <th scope="col">
                            {{ $trans.get('__JSON__.Ends At') }}
                        </th>
                        <th scope="col">
                            {{ $trans.get('__JSON__.Job') }}
                        </th>
                        <th scope="col">
                            {{ $trans.get('__JSON__.Employee') }}
                        </th>
                        <th scope="col">
                            {{ $trans.get('__JSON__.Mutation') }}
                        </th>
                        <th scope="col">
                            {{ $trans.get('__JSON__.Worked Hours') }}
                        </th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody v-if="loading">
                    <tr>
                        <th colspan="7" class="text-center font-italic">
                            <div class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">{{ $trans.get('__JSON__.Loading...') }}</span>
                            </div>
                        </th>
                    </tr>
                    </tbody>
                    <tbody v-else>
                    <tr v-if="sortedEnrollments.length === 0">
                        <th colspan="7" class="text-center font-italic">
                            {{ $trans.get('__JSON__.No enrollments yet') }}
                        </th>
                    </tr>
                    <tr v-for="enrollment in sortedEnrollments" :ref="`row_${enrollment.id}`">
                        <td v-html="enrollment.start_human"></td>
                        <td v-html="enrollment.end_human"></td>
                        <td>
                            <span class="text-heading font-semibold d-block">
                                {{ enrollment.job }}
                            </span>
                            <span class="d-block">
                                {{ enrollment.location }}
                            </span>
                        </td>
                        <td>
                            {{ enrollment.employee }}
                        </td>
                        <td :class="{ 'clickable': enrollment.mutations.length > 0 }" @click="showMutation(enrollment)">
                            <span v-if="enrollment.mutations.length === 0">-</span>
                            <span class="badge bg-danger" v-if="enrollment.status === 'conflict'">{{ enrollment.status }}</span>
                            <span class="d-block" v-for="record in enrollment.mutations">
                                {{ $trans.get('__JSON__.:amount minutes', { amount: record.mutationHuman }) }}
                                <span class="badge" :class="{ 'bg-dark': record.type === 'knikkers', 'bg-secondary': record.type === 'client', 'bg-warning': record.type === 'employee' }">
                                    {{ record.type }}
                                </span>
                            </span>
                        </td>
                        <td class="font-bold">
                            <span v-if="enrollment.status === 'conflict'">?</span>
                            <span v-else-if="enrollment.mutations.length > 0 && enrollment.status !== 'conflict'">
                                {{ enrollment.total.mutated }}
                            </span>
                            <span v-else>
                                {{ enrollment.total.original }}
                            </span>
                        </td>
                        <td class="text-end">
                            <div class="btn-group">
                                <button type="button" v-for="action in enrollment.actions" class="btn btn-sm btn-neutral" :class="action.classes" @click="handleAction(enrollment, action)" :ref="`${enrollment.id}-${action.id}`">
                                    {{ action.label }}
                                </button>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>

    <MutationCorrectionModal :user="user" :enrollment="enrollment" ref="correct" v-if="enrollment" />
    <MutationDetailsModal :enrollment="enrollment" v-if="enrollment" />
</template>

<script>
import MutationCorrectionModal from '@/components/shared/MutationCorrectionModal'
import MutationDetailsModal from '@/components/shared/MutationDetailsModal'
import ModalHelper from "./mixins/ModalHelper";

export default {
    name: 'Mutations',
    components: { MutationCorrectionModal, MutationDetailsModal },
    mixins: [ModalHelper],
    data() {
        return {
            loading: false,
            startOfWeek: null,
            dayOfWeek: 'monday',
            enrollment: null,
            user: null,
            weeks: [],
            weekdays: [],
            enrollments: [],
            tooltips: [],
        }
    },
    computed: {
        sortedEnrollments() {
            return this.enrollments
                .filter(enrollment => enrollment.day_of_week === this.dayOfWeek)
                .sort((a, b) => b.start_time > a.start_time ? -1 : 1)
        },
    },
    watch: {
        startOfWeek() {
            this.dayOfWeek = 'monday'
        },
        enrollments: {
            handler() {
                this.tooltips = [].slice.call(document.querySelectorAll('[tooltip]'))
                    .map(element => new bootstrap.Tooltip(element));
            },
            deep: true,
        },
    },
    methods: {
        retrieveEnrollments() {
            this.loading = true
            axios.post('/api/mutations', { start_of_week: this.startOfWeek })
                .then(response => {
                    const data = response.data
                    this.weekdays = data.weekdays
                    this.enrollments = data.enrollments
                    this.weeks = data.weeks
                    this.user = data.user
                })
                .finally(() => this.loading = false)
        },
        handleAction(enrollment, action) {
            this.enrollment = enrollment
            if (action.id === 'correct') setTimeout(async () => await this.openModal('correct_modal'), 50)
        },
        performAction(action, data = {}) {
            if (action === 'correct') {
                const correct = this.$refs.correct,
                    button = correct.$refs.submit

                return this.makeRequest('correct', button, data, () => {
                    window.bootstrap.Modal.getOrCreateInstance(document.getElementById('correct_modal')).hide()
                    this.retrieveEnrollments()
                })
            }
        },
        makeRequest(action, button, context = {}, closure = () => {}) {
            let data = {
                action,
                enrollment: this.enrollment.id,
            }

            const buttonText = button.innerHTML
            button.setAttribute('disabled', true)
            button.innerHTML = '<div class="spinner-border spinner-border-sm text-primary" role="status"></div>'

            return axios.post('/api/mutations/action', Object.assign(data, context))
                .then(response => closure(response))
                .catch(error => window.toastr.error(error.response.data.message ?? this.$trans.get('__JSON__.Something went wrong'), '', {
                    positionClass: 'toast-bottom-right',
                }))
                .finally(() => {
                    button.innerHTML = buttonText
                    button.removeAttribute('disabled')
                })
        },
        changeWeek(direction) {
            this.startOfWeek = this.weeks[direction].start
            this.retrieveEnrollments()
        },
        async showMutation(enrollment) {
            if (enrollment.mutations.length === 0) return
            this.enrollment = enrollment
            setTimeout(async () => await this.openModal('mutation_modal'), 100)
            // await window.bootstrap.Modal.getOrCreateInstance(document.getElementById('mutation_modal')).show()
        },
    },
    mounted() {
        this.retrieveEnrollments()
    },
}
</script>
